// Paid-door attribution (#253): capture the UTMs an ad click lands with and
// carry them across the Auth0 signup round-trip (same approach as trialIntent
// and ?ref) into checkout, where the backend stamps them onto the Stripe
// subscription. The server-side `trial_started` event reads them back from
// there, so a trial can be scored against the campaign that opened the door
// without trusting client-side PostHog to have identified the visitor.
//
// Last touch wins: a second ad click replaces the first. Entries older than
// ATTRIBUTION_TTL_MS are ignored rather than credited to a stale campaign.

export const ATTRIBUTION_KEY = 'pev_attribution';
export const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
// Mirrors the server's sanitiser (app/trial_events.py) so what is stored is
// what will be stamped.
const VALUE_RE = /^[A-Za-z0-9._-]{1,100}$/;

const getStore = (storage) =>
  storage || (typeof window !== 'undefined' ? window.localStorage : null);

/** The valid utm_* params in a location.search string, or null if there are none. */
export const parseUtms = (search) => {
  try {
    const params = new URLSearchParams(search || '');
    const utms = {};
    UTM_KEYS.forEach((key) => {
      const value = (params.get(key) || '').trim();
      if (VALUE_RE.test(value)) utms[key] = value;
    });
    return Object.keys(utms).length ? utms : null;
  } catch (e) {
    return null;
  }
};

/** Persist the UTMs from `search` with the landing path. Returns what was stored, or null. */
export const captureAttribution = (search, landingPath, storage, now = Date.now()) => {
  const utms = parseUtms(search);
  const store = getStore(storage);
  if (!utms || !store) return utms;
  const entry = { ...utms, landing_path: landingPath, captured_at: now };
  try { store.setItem(ATTRIBUTION_KEY, JSON.stringify(entry)); } catch (e) { /* storage unavailable */ }
  return entry;
};

/** The stored UTMs plus landing_path, or null when absent, malformed or expired. */
export const getStoredAttribution = (storage, now = Date.now()) => {
  const store = getStore(storage);
  try {
    const raw = store ? store.getItem(ATTRIBUTION_KEY) : null;
    if (!raw) return null;
    const { captured_at: capturedAt, ...rest } = JSON.parse(raw);
    if (!Number.isFinite(capturedAt) || now - capturedAt > ATTRIBUTION_TTL_MS) return null;
    return rest;
  } catch (e) {
    return null;
  }
};

/** The UTMs to attribute an email capture to: the stored ad click's, else the current URL's. */
export const currentUtms = (search = typeof window !== 'undefined' ? window.location.search : '', storage) => {
  const found = getStoredAttribution(storage) || parseUtms(search) || {};
  return UTM_KEYS.reduce((acc, key) => (found[key] ? { ...acc, [key]: found[key] } : acc), {});
};
